import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { QrCode, Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface UpiQrCodeProps {
  upiId: string;
  payeeName: string;
  amount?: number;
  currency?: string;
  transactionNote?: string;
  className?: string;
}

export function UpiQrCode({
  upiId,
  payeeName,
  amount,
  currency = "INR",
  transactionNote,
  className = "",
}: UpiQrCodeProps) {
  const [showQrDialog, setShowQrDialog] = useState(false);

  // Generate UPI deep link URL for QR code
  const generateUpiUrl = () => {
    const params = new URLSearchParams();
    params.set("pa", upiId);
    params.set("pn", payeeName);
    if (amount && amount > 0) {
      params.set("am", amount.toFixed(2));
    }
    params.set("cu", currency);
    if (transactionNote) {
      params.set("tn", transactionNote);
    }
    return `upi://pay?${params.toString()}`;
  };

  const upiUrl = generateUpiUrl();

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className={`gap-2 ${className}`}
        onClick={() => setShowQrDialog(true)}
      >
        <QrCode className="w-4 h-4" />
        Show QR Code
      </Button>

      <Dialog open={showQrDialog} onOpenChange={setShowQrDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              Scan to Pay
            </DialogTitle>
            <DialogDescription>
              Scan this QR code with any UPI app to make payment
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center py-4 space-y-4">
            {/* QR Code */}
            <div className="bg-white p-4 rounded-xl shadow-sm border">
              <QRCodeSVG
                value={upiUrl}
                size={200}
                level="H"
                includeMargin={false}
              />
            </div>

            {/* Payment Details */}
            <div className="text-center space-y-1">
              {amount && amount > 0 && (
                <p className="text-2xl font-bold">
                  {currency} {amount.toFixed(2)}
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                Pay to: <span className="font-medium text-foreground">{upiId}</span>
              </p>
              {transactionNote && (
                <p className="text-xs text-muted-foreground">
                  For: {transactionNote}
                </p>
              )}
            </div>

            {/* Info Alert */}
            <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800">
              <Info className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-xs text-amber-800 dark:text-amber-200">
                <strong>Note:</strong> After payment, please upload a screenshot as proof. 
                The organizer will verify and mark your payment as complete.
              </AlertDescription>
            </Alert>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
